import { Component, Inject, NgZone, OnInit, PLATFORM_ID } from '@angular/core';
import {
  DataService,
  PamlabApiServiceService,
  RouteService,
  SnackbarService,
} from '@/shared/services';
import { Subscription } from 'rxjs';
import { EntryExit } from '../../../../../server/types';
import * as am5 from '@amcharts/amcharts5';
import * as am5flow from '@amcharts/amcharts5/flow';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'entry-exit-flow-info-content',
  templateUrl: './entry-exit-flow-info-content.component.html',
  styleUrls: ['./entry-exit-flow-info-content.component.css'],
  standalone: false,
})
export class EntryExitFlowInfoContentComponent implements OnInit {
  public dataPending!: boolean;
  private subscription: Subscription = new Subscription();
  public root!: am5.Root;
  // data series variable
  private series!: am5flow.Sankey;
  dataLoaded: boolean = false;
  entryExitData!: EntryExit[];
  tooltipMessage: string =
    'Forward: Gives the movement of student from selected college/department/program;\nBackward: The flow of students who graduated into the selected college or program;';

  constructor(
    public dataService: DataService,
    private apiService: PamlabApiServiceService,
    private snackbarService: SnackbarService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
    private routeService: RouteService,
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.dataService.getData().subscribe((dataStream) => {
        if (dataStream.data) {
          // TODO: The chart shows if the length of data is less than 116 elements
          // Needs to increase height if length of data is more than 116
          this.entryExitData = this.generateDummyNodes(
            dataStream.data as EntryExit[],
          );
          this.generateGraph();
        }

        dataStream.error &&
          this.snackbarService.open(dataStream.error.message, 'error');
      }),
    );
    this.subscription.add(
      this.dataService.getPendingData().subscribe((pending) => {
        this.dataPending = pending;
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.browserOnly(() => {
      if (this.root) {
        this.root.dispose();
      }
    });
  }

  browserOnly(f: () => void) {
    if (isPlatformBrowser(this.platformId)) {
      this.zone.runOutsideAngular(() => {
        f();
      });
    }
  }

  generateGraph() {
    // if chart is not empty then erase it for new data coming
    if (this.root) {
      this.root.container.children.pop();
    }

    // Create root and chart element
    if (!this.root) this.root = am5.Root.new('chartdiv');

    // Remove amcharts logo below graphs
    this.root._logo!.dispose();

    // Set themes
    this.root.setThemes([am5themes_Animated.new(this.root)]);

    // Create series
    this.series = this.root.container.children.push(
      am5flow.Sankey.new(this.root, {
        sourceIdField: 'from',
        targetIdField: 'to',
        valueField: 'value',
        nodeWidth: 15,
        nodePadding: 50,
        paddingRight: 150,
        paddingBottom: 50,
        showTooltipOn: 'hover',
      }),
    );

    // Set tooltip label on nodes
    this.series.nodes.rectangles.template.adapters.add(
      'tooltipText',
      (_text, target) => {
        const dataContext = target.dataItem?.dataContext as {
          id: string;
          unknown: boolean;
        };

        if (!dataContext) {
          return undefined;
        }

        const nodes = this.entryExitData.filter(
          (data) => data.from === dataContext.id,
        );
        let sumNodeText = 'sumOutgoing';

        if (!nodes.length) {
          sumNodeText = 'sumIncoming';
        }

        return dataContext.id.includes('label')
          ? `{${sumNodeText}}`
          : `[bold]{name}[/]\nTotal: {${sumNodeText}}`;
      },
    );

    this.series.nodes.labels.template.adapters.add('text', (_value, target) => {
      const dataContext = target.dataItem?.dataContext as {
        id: string;
        unknown: boolean;
      };

      if (!dataContext) {
        return undefined;
      }

      const nodes = this.entryExitData.filter(
        (data) => data.from === dataContext.id,
      );
      let sumNodeText = 'sumOutgoing';

      if (!nodes.length) {
        sumNodeText = 'sumIncoming';
      }

      return dataContext.id.includes('label')
        ? `{${sumNodeText}}`
        : `{name}\nTotal: {${sumNodeText}}`;
    });

    // Disable tooltips on nodes (rectangles)
    this.series.nodes.rectangles.template.setAll({
      tooltipText: '', // Removes tooltip
    });

    // Disable tooltips on links (branches)
    this.series.links.template.setAll({
      tooltipText: '', // Removes tooltip
    });

    // Hide dummy nodes vertical bar
    this.series.nodes.rectangles.template.adapters.add(
      'fillOpacity',
      (opacity, target) => {
        if (!target || !target.dataItem) return undefined;

        const dataContext = target.dataItem.dataContext as {
          id: string;
          unknown: boolean;
        };

        return dataContext.id.includes('label') ? 0 : opacity;
      },
    );
    this.series.nodes.rectangles.template.adapters.add(
      'strokeOpacity',
      (opacity, target) => {
        if (!target || !target.dataItem) return undefined;

        const dataContext = target.dataItem.dataContext as {
          id: string;
          unknown: boolean;
        };

        return dataContext.id.includes('label') ? 0 : opacity;
      },
    );

    this.series.nodes.get('colors')!.set('step', 2);

    // Set data
    this.series.data.setAll(this.entryExitData);

    this.series.nodes.rectangles.template.setAll({
      fillOpacity: 0.5,
      stroke: am5.color(0x000000),
      strokeWidth: 1,
      cornerRadiusTL: 4,
      cornerRadiusTR: 4,
      cornerRadiusBL: 4,
      cornerRadiusBR: 4,
    });

    this.series.links.template.setAll({
      tooltipText:
        'From: [bold]{sourceId}[/]\nTo: [bold]{targetId}\nValue: [bold]{value}[/]',
    });

    this.series.nodes.labels.template.setAll({
      x: am5.percent(-10),
      centerX: am5.percent(-10),
      maxWidth: 150,
      oversizedBehavior: 'wrap',
      fontSize: 15,
    });

    this.series.nodes.nodes.template.setAll({
      toggleKey: 'none', // disables toggling
    });
  }

  generateDummyNodes(data: EntryExit[]): EntryExit[] {
    const enhancedData: EntryExit[] = [];
    const outgoingNodes = new Set();
    const incomingNodes = new Set();
    const dummyNodes: EntryExit[] = [];

    // Track nodes with outgoing and incoming links
    data.forEach(({ from, to }) => {
      outgoingNodes.add(from);
      incomingNodes.add(to);
    });

    // Find ending nodes (nodes that only receive links but do not send any)
    const endingNodes = [...incomingNodes].filter(
      (node) => !outgoingNodes.has(node),
    );

    // Generate dummy nodes for links that are not ending nodes
    data.forEach(({ from, to, value }) => {
      if (!endingNodes.includes(from)) {
        const dummyNode = `${from}_${to}_label`;
        dummyNodes.push({ from, to: dummyNode, value }); // Dummy node
        dummyNodes.push({ from: dummyNode, to, value }); // Assign value to dummy node
      } else {
        enhancedData.push({ from, to, value }); // Keep original link for ending nodes
      }
    });

    // return [...enhancedData, ...dummyNodes];
    return data;
  }

  navigateToGlossaryOfTerms(termValue: string) {
    this.routeService.navigateNewTab('/glossary-of-terms', {
      term_value: termValue,
    });
  }
}
